import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@common-fe/common-fe';
import { AppButton, Box, Preloader } from '@common-fe/common-fe';
import _ from 'lodash';

import ROUTES from '@/common/routes';
import { API_URL, IClaimRepaymentDtoStatusEnum } from '@/dtos';
import { IReturnMailDetailsProcessingActionEnum } from '@/dtos/models/IReturnMailDetails';
import { useHistory } from '@/modules/core/hooks';
import {
  DestinationType,
  LockboxServerErrorText,
  LockboxStatus,
  LockboxStatusReason,
  lockboxStatusTitle,
  LockboxType,
  LockboxValidationError,
  WireStatus,
} from '@/modules/employer/components/Lockbox/lockbox.types';
import {
  LockboxErrorModal,
  WireSentModal,
} from '@/modules/employer/components/Lockbox/LockboxDetails/components';
import useValidateLockbox from '@/modules/employer/components/Lockbox/LockboxDetails/hooks/useValidateLockbox';
import useHighlightLockboxStatusStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useHighlightLockboxStatus.store';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import useUpdateLockboxQuery from '@/modules/employer/components/Lockbox/queries/useUpdateLockbox.query';
import theme from '@/styles/theme';

import { useGetForBenefitOf } from '../LockboxProcessing/components/ForBenefitOf';
import ReissueReturnedLockboxCheckModal from '../ReissueReturnedLockboxCheckModal/ReissueReturnedLockboxCheckModal';
import VoidReturnedLockboxCheckModal from '../VoidReturnedLockboxCheckModal/VoidReturnedLockboxCheckModal';

const BUTTON_WIDTH = '180px';

const LockboxProcessingFooterActions = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [hasFieldsValidation, setHasFieldsValidation] = useState(false);
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [isWireSentModalVisible, setIsWireSentModalVisible] = useState(false);
  const [isVoidCheckPopupVisible, setIsVoidCheckPopupVisible] = useState(false);
  const [isReissueCheckPopupVisible, setIsReissueCheckPopupVisible] = useState(false);
  const { validateLockboxStatus, validateLockboxFields } = useValidateLockbox();
  const { setHighlightStatus } = useHighlightLockboxStatusStore();
  const {
    editableLockbox,
    originalLockbox,
    validationErrors,
    nextLockboxId,
    isEditMode,
    setNextLockboxId,
    setValidationErrors,
    pushValidationErrors,
    reset,
  } = useLockboxDetailsStore();
  const { updateLockbox, isLoading, error } = useUpdateLockboxQuery({ lockboxId: originalLockbox?.id });

  const showWireSentButton = useMemo(
    () =>
      originalLockbox?.lockboxType === LockboxType.OUTBOUND_TOA &&
      originalLockbox?.lockboxStatus === LockboxStatus.APPROVED &&
      originalLockbox?.wireStatusType === WireStatus.NOT_SENT,
    [originalLockbox]
  );

  const isStatusValidationSuccessfully = useMemo(() => {
    return validateLockboxStatus(editableLockbox)?.length === 0;
  }, [editableLockbox, validateLockboxStatus]);

  const buttonColor = useMemo(() => {
    if (editableLockbox?.lockboxType === LockboxType.RETURNED_MAIL) {
      return theme.colors.buttonPrimary;
    }
    switch (editableLockbox?.lockboxStatus) {
      case LockboxStatus.APPROVED: {
        return theme.colors.success;
      }
      case LockboxStatus.PARTIALLY_APPROVED: {
        return theme.colors.success;
      }
      case LockboxStatus.DECLINED: {
        return theme.colors.danger;
      }
      case LockboxStatus.VOID: {
        return theme.colors.danger;
      }
      case LockboxStatus.CANCELED: {
        return theme.colors.danger;
      }
      case LockboxStatus.PENDING: {
        return theme.colors.warning;
      }
      default: {
        return theme.colors.buttonPrimary;
      }
    }
  }, [editableLockbox]);

  const buttonTitle = useMemo(() => {
    if (!isStatusValidationSuccessfully) {
      return 'Select an option';
    }

    if (
      editableLockbox?.lockboxType === LockboxType.RETURNED_MAIL
      && editableLockbox?.lockboxStatus === LockboxStatus.APPROVED
    ) {
      return 'Process';
    }

    if (editableLockbox?.lockboxType === LockboxType.RETURNED_CHECK
      && editableLockbox?.lockboxStatus === LockboxStatus.PROCESS
    ) {
      return 'Link Check';
    }

    if (editableLockbox?.lockboxStatus === LockboxStatus.PROCESSED_MANUALLY) {
      return 'Process';
    }

    if (editableLockbox?.lockboxStatus === LockboxStatus.VOID) {
      return 'Void';
    }

    return editableLockbox?.lockboxStatus ? lockboxStatusTitle[editableLockbox?.lockboxStatus] : '';
  }, [editableLockbox, isStatusValidationSuccessfully]);

  const isFieldsValidationSuccessfully = useMemo(() => {
    return validationErrors?.length === 0;
  }, [validationErrors]);

  const handleNextRecord = useCallback(() => {
    setNextLockboxId(undefined);
    queryClient.removeQueries({
      queryKey: [API_URL.LOCKBOXES_BY_LOCKBOX_ID(originalLockbox?.id || '')],
    });
    reset();
    history.push(ROUTES.LOCKBOX_DETAILS(nextLockboxId));
  }, [history, nextLockboxId, setNextLockboxId, reset, queryClient, originalLockbox?.id]);

  const handleOpenWireSentModal = useCallback(() => {
    setIsWireSentModalVisible(true);
  }, []);

  const forBenefitOfStatus = useGetForBenefitOf();

  const remainder = useMemo(() => {
    if (!Object.keys(editableLockbox?.remainder || {})?.length) {
      return;
    }

    if (editableLockbox?.remainder?.destinationType === DestinationType.PLAN_ACCOUNT) {
      return {
        destination_type: editableLockbox?.remainder?.destinationType,
        account_id: editableLockbox?.remainder?.accountId,
      };
    }

    if (editableLockbox?.remainder?.destinationType === DestinationType.CHECK) {
      return {
        destination_type: editableLockbox?.remainder?.destinationType,
        benefit_of: editableLockbox?.remainder?.benefitOf,
        pay_to: editableLockbox?.remainder?.payTo,
        address_line1: editableLockbox?.remainder?.addressLine1,
        address_line2: editableLockbox?.remainder?.addressLine2,
        city: editableLockbox?.remainder?.city,
        state: editableLockbox?.remainder?.state,
        zip: editableLockbox?.remainder?.zip,
      };
    }
  }, [editableLockbox]);

  const isVoidCheck = useMemo(() => editableLockbox?.lockboxType === LockboxType.RETURNED_CHECK
    && editableLockbox?.lockboxStatus === LockboxStatus.VOID,
    [editableLockbox?.lockboxType, editableLockbox?.lockboxStatus]);

  const isReissueCheck = useMemo(() => editableLockbox?.lockboxType === LockboxType.RETURNED_CHECK
    && editableLockbox?.lockboxStatus === LockboxStatus.REISSUE,
    [editableLockbox?.lockboxType, editableLockbox?.lockboxStatus]);

  const isProcessClaimCheck = useMemo(() => editableLockbox?.lockboxType === LockboxType.RETURNED_CHECK
    && editableLockbox?.lockboxStatus === LockboxStatus.PROCESS,
    [editableLockbox?.lockboxType, editableLockbox?.lockboxStatus]);
  
  const handleUpdateLockbox = useCallback(async () => {
    const isPaymentMethod =
      editableLockbox?.lockboxType === LockboxType.OUTBOUND_TOA &&
      editableLockbox?.lockboxStatus === LockboxStatus.APPROVED;
    const isWirePaymentType = editableLockbox?.reason === LockboxStatusReason.WIRE;

    setHasFieldsValidation(true);
    const formattedEditableAmount = editableLockbox?.amount
      ? parseFloat(editableLockbox?.amount).toFixed(2)
      : editableLockbox?.amount;

    const formattedOriginalAmount = originalLockbox?.amount
      ? parseFloat(originalLockbox?.amount).toFixed(2)
      : originalLockbox?.amount;

    const updateLockboxData = {
      lockbox_type: editableLockbox?.lockboxType,
      ...(formattedEditableAmount !== formattedOriginalAmount
        ? { amount: formattedEditableAmount }
        : { amount: originalLockbox?.amount }),
      employee_id: editableLockbox?.employeeId,
      account_id: editableLockbox?.accountId,
      lockbox_status: isVoidCheck || isReissueCheck || isProcessClaimCheck
        ? LockboxStatus.APPROVED
        : editableLockbox?.lockboxStatus,
      vendor_id: editableLockbox?.vendorId,
      ...(isPaymentMethod
        ? {
            payment_method_type: editableLockbox?.reason,
            ...(isWirePaymentType ? { wire_status_type: WireStatus.NOT_SENT } : {}),
          }
        : {
            reason:
              (editableLockbox?.lockboxStatus === LockboxStatus.PARTIALLY_APPROVED || isVoidCheck || isReissueCheck || isProcessClaimCheck)
                ? undefined
                : editableLockbox?.reason,
          }),
      notes: editableLockbox?.notes,
      ...(editableLockbox?.lockboxType === LockboxType.HSA_FORM
        ? { form_name: editableLockbox?.formName }
        : {}),
      liquidate_all_funds: editableLockbox?.liquidateAllFunds,
      ...isReissueCheck ? {} : { remainder },
      ...(editableLockbox?.claimRepayments?.length
        ? {
            claim_repayments: editableLockbox?.claimRepayments?.map((claimRepayment) => ({
              claim_id: claimRepayment?.claimId,
              repayment_amount: claimRepayment?.repaymentAmount,
              employee_id: claimRepayment?.employeeId,
              organization_id: claimRepayment?.organizationId,
              organization_path: claimRepayment?.organizationPath,
              status: IClaimRepaymentDtoStatusEnum.CREATED,
            })),
          }
        : { claim_repayments: [] }),
      ...(editableLockbox?.lockboxType === LockboxType.RETURNED_MAIL ? {
        details: {
          type: LockboxType.RETURNED_MAIL,
          original_mailing_type: editableLockbox?.mailingType
            || originalLockbox?.details?.originalMailingType
            || originalLockbox?.ocr?.details?.originalMailingType,
          processing_action: editableLockbox?.lockboxStatus === LockboxStatus.APPROVED
            ? IReturnMailDetailsProcessingActionEnum.PROCESS
            : undefined,
        },
      } : {}),
      ...(isVoidCheck ? {
        details: {
          type: editableLockbox?.lockboxType,
          check_id: editableLockbox?.selectedCheckId,
          processing_decision: {
            type: editableLockbox?.lockboxStatus,
            reason: editableLockbox?.reason,
          },
        },
      } : {}),
      ...(isReissueCheck ? {
        details: {
          type: editableLockbox?.lockboxType,
          pay_to: editableLockbox?.remainder?.payTo,
          check_id: editableLockbox?.selectedCheckId,
          processing_decision: {
            type: editableLockbox?.lockboxStatus,
            benefit_of: editableLockbox?.remainder?.benefitOf,
            address: {
              line1: editableLockbox?.remainder?.addressLine1,
              line2: editableLockbox?.remainder?.addressLine2,
              city: editableLockbox?.remainder?.city,
              state: editableLockbox?.remainder?.state,
              zip: editableLockbox?.remainder?.zip,
            },
          },
        },
      } : {}),
      ...(isProcessClaimCheck ? {
        details: {
          type: editableLockbox?.lockboxType,
          check_id: editableLockbox?.selectedCheckId,
        }
      } : {})
    };
    const cleanedUpdateLockboxData = _.omitBy(
      updateLockboxData,
      (value) => value === null || value === undefined || value === ''
    );

    await updateLockbox({
      ...cleanedUpdateLockboxData,
      account_id: editableLockbox?.accountId,
      ...(forBenefitOfStatus ? { check_benefit_of: editableLockbox?.checkBenefitOf } : {}),
    });
    setHighlightStatus(editableLockbox?.lockboxStatus);
  }, [
    updateLockbox,
    editableLockbox,
    originalLockbox,
    setHighlightStatus,
    forBenefitOfStatus,
    remainder,
    isVoidCheck,
    isReissueCheck,
    isProcessClaimCheck,
  ]);

  const handleSubmitForm = useCallback(async () => {
    const fieldsErrors = validateLockboxFields(editableLockbox);

    if (fieldsErrors?.length > 0) {
      setHasFieldsValidation(true);
      return;
    }

    if (editableLockbox?.lockboxType === LockboxType.RETURNED_CHECK
      && editableLockbox?.lockboxStatus === LockboxStatus.VOID
      && editableLockbox?.selectedCheckId
    ) {
      setIsVoidCheckPopupVisible(true);
      return;
    }

    if (editableLockbox?.lockboxType === LockboxType.RETURNED_CHECK
      && editableLockbox?.lockboxStatus === LockboxStatus.REISSUE
      && editableLockbox?.selectedCheckId
    ) {
      setIsReissueCheckPopupVisible(true);
      return;
    }

    await handleUpdateLockbox();
  }, [editableLockbox, validateLockboxFields, handleUpdateLockbox]);

  useEffect(() => {
    if (hasFieldsValidation) {
      setValidationErrors(validateLockboxFields(editableLockbox));
    }
  }, [hasFieldsValidation, editableLockbox, validateLockboxFields, setValidationErrors]);

  useEffect(() => {
    if (!error) {
      return;
    }

    const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;

    if (errorMessage.includes(LockboxServerErrorText.INVESTMENTS_MUST_BE_LIQUIDATED)) {
      pushValidationErrors([LockboxValidationError.INVESTMENTS_MUST_BE_LIQUIDATED]);
      return;
    }

    setIsErrorModalVisible(true);
  }, [error, pushValidationErrors]);

  return (
    <>
      {showWireSentButton && (
        <Box margin={{ right: 'spacing12' }}>
          <AppButton
            testId="wire-sent"
            width={BUTTON_WIDTH}
            buttonType="secondary"
            onClick={handleOpenWireSentModal}
          >
            Wire Sent
          </AppButton>
        </Box>
      )}
      {isEditMode ? (
        <Box>
          <AppButton
            testId="lockbox-processing"
            width={BUTTON_WIDTH}
            onClick={handleSubmitForm}
            disabled={!isStatusValidationSuccessfully || !isFieldsValidationSuccessfully || isLoading}
            {...(isStatusValidationSuccessfully && isFieldsValidationSuccessfully && !isLoading
              ? { style: { backgroundColor: buttonColor } }
              : {})}
          >
            {isLoading ? <Preloader color="white" /> : buttonTitle}
          </AppButton>
        </Box>
      ) : (
        <Box>
          <AppButton
            testId="next-lockbox"
            width={BUTTON_WIDTH}
            onClick={handleNextRecord}
            disabled={!nextLockboxId}
          >
            Next record
          </AppButton>
        </Box>
      )}

      {isWireSentModalVisible && <WireSentModal onSetVisible={setIsWireSentModalVisible} />}

      {isErrorModalVisible && (
        <LockboxErrorModal onSubmit={handleSubmitForm} onSetVisible={setIsErrorModalVisible} isAwaitMode />
      )}

      {isVoidCheckPopupVisible && (
        <VoidReturnedLockboxCheckModal
          isVisible={isVoidCheckPopupVisible}
          setIsVisible={setIsVoidCheckPopupVisible}
          checkId={editableLockbox?.selectedCheckId}
          onUpdateLockbox={handleUpdateLockbox}
        />
      )}

      {isReissueCheckPopupVisible && (
        <ReissueReturnedLockboxCheckModal
          isVisible={isReissueCheckPopupVisible}
          setIsVisible={setIsReissueCheckPopupVisible}
          onUpdateLockbox={handleUpdateLockbox}
          check={editableLockbox?.check}
        />
      )}
    </>
  );
};

export default LockboxProcessingFooterActions;
